import { useEffect, useMemo, useState } from "react";
import {
  Boxes,
  AlertTriangle,
  PackageCheck,
  Factory,
  Plus,
  Pencil,
  Trash2,
  Search,
  Scale,
  DollarSign,
  Truck,
  ArrowDownToLine,
  ClipboardList,
  ArrowDownCircle,
  CheckCircle2,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/useAuth";
import { useDebounce } from "@/hooks/useDebounce";
import { useInventory } from "@/hooks/useInventory";
import { InventoryAlertSettingsCard } from "@/components/inventory/InventoryAlertSettingsCard";
import {
  INVENTORY_UNITS,
  InventoryAlertHistory,
  InventoryItem,
  InventoryMovement,
  ReplenishmentRecommendation,
  StationSupplyRequirement,
} from "@/types/inventory";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePageSkeleton } from "@/components/shared/TablePageSkeleton";

const emptyMaterialForm = {
  name: "",
  sku: "",
  unit_of_measure: "unidad",
  current_stock: "0",
  minimum_stock: "0",
  purchase_price: "0",
  reference_price: "0",
  description: "",
  es_material_cliente: false,
};

const emptyStationForm = {
  station_name: "",
  inventory_id: "",
  estimated_quantity: "1",
  notes: "",
};

const emptyRestockForm = {
  inventory_id: "",
  movement_type: "restock" as "restock" | "adjustment",
  quantity: "1",
  unit_cost: "",
  notes: "",
};

const emptyConsumptionForm = {
  station_name: "",
  produced_units: "1",
  order_reference: "",
  notes: "",
};

const getStockBadgeClass = (isLowStock: boolean) =>
  isLowStock
    ? "bg-rose-100 text-rose-700 border-rose-200"
    : "bg-emerald-100 text-emerald-700 border-emerald-200";

const getAlertLevelBadgeClass = (level: string) => {
  switch (level) {
    case "critical":
      return "bg-rose-100 text-rose-700 border-rose-200";
    case "high":
      return "bg-amber-100 text-amber-700 border-amber-200";
    case "medium":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    default:
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
  }
};

const formatMovementType = (type: string) => {
  switch (type) {
    case "restock":
      return "Reposición";
    case "consumption":
      return "Consumo";
    case "adjustment":
      return "Ajuste";
    default:
      return type;
  }
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "N/D";
  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function Inventory() {
  const { company, can } = useAuth();
  const slug = company?.slug || "";
  const companyId = company?.id || "";
  const canReadInventory = can("inventory.read");
  const canManageInventory = can("inventory.manage");
  const canConfigureInventory = can("inventory.configure");

  const {
    inventoryItems,
    isLoadingInventoryItems,
    stationConfigurations,
    isLoadingStationConfigurations,
    replenishmentDashboard,
    isLoadingReplenishmentDashboard,
    inventoryMovements,
    isLoadingInventoryMovements,
    createInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
    createStationConfiguration,
    updateStationConfiguration,
    deleteStationConfiguration,
    createInventoryMovement,
    consumeStationSupplies,
  } = useInventory(slug);

  const [activeTab, setActiveTab] = useState("materials");
  const [materialSearch, setMaterialSearch] = useState("");
  const [stationSearch, setStationSearch] = useState("");
  const debouncedMaterialSearch = useDebounce(materialSearch, 250);
  const debouncedStationSearch = useDebounce(stationSearch, 250);

  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const [isStationDialogOpen, setIsStationDialogOpen] = useState(false);
  const [isRestockDialogOpen, setIsRestockDialogOpen] = useState(false);
  const [isConsumptionDialogOpen, setIsConsumptionDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<InventoryItem | null>(null);
  const [editingStationConfig, setEditingStationConfig] =
    useState<StationSupplyRequirement | null>(null);
  const [materialForm, setMaterialForm] = useState(emptyMaterialForm);
  const [stationForm, setStationForm] = useState(emptyStationForm);
  const [restockForm, setRestockForm] = useState(emptyRestockForm);
  const [consumptionForm, setConsumptionForm] = useState(emptyConsumptionForm);
  const [consumptionStep, setConsumptionStep] = useState<"info" | "review">("info");
  const [consumedItems, setConsumedItems] = useState<Array<{
    inventory_id: string;
    material_name: string;
    unit_of_measure: string;
    estimated: number;
    real: number;
    waste: number;
  }>>([]);

  useEffect(() => {
    if (!isMaterialDialogOpen) {
      setEditingMaterial(null);
      setMaterialForm(emptyMaterialForm);
    }
  }, [isMaterialDialogOpen]);

  useEffect(() => {
    if (!isStationDialogOpen) {
      setEditingStationConfig(null);
      setStationForm(emptyStationForm);
    }
  }, [isStationDialogOpen]);

  useEffect(() => {
    if (!isRestockDialogOpen) {
      setRestockForm(emptyRestockForm);
    }
  }, [isRestockDialogOpen]);

  useEffect(() => {
    if (!isConsumptionDialogOpen) {
      setConsumptionForm(emptyConsumptionForm);
      setConsumptionStep("info");
      setConsumedItems([]);
    }
  }, [isConsumptionDialogOpen]);

  const filteredMaterials = useMemo(() => {
    const query = debouncedMaterialSearch.trim().toLowerCase();
    if (!query) return inventoryItems;

    return inventoryItems.filter((item: InventoryItem) =>
      [item.name, item.sku, item.unit_of_measure]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [inventoryItems, debouncedMaterialSearch]);

  const filteredStationConfigurations = useMemo(() => {
    const query = debouncedStationSearch.trim().toLowerCase();
    if (!query) return stationConfigurations;

    return stationConfigurations.filter((config: StationSupplyRequirement) =>
      [
        config.station_name,
        config.inventoryItem?.name || "",
        config.inventoryItem?.sku || "",
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [stationConfigurations, debouncedStationSearch]);

  const lowStockItems = useMemo(
    () => inventoryItems.filter((item: InventoryItem) => item.is_low_stock),
    [inventoryItems],
  );

  const uniqueStationsCount = useMemo(
    () => new Set(stationConfigurations.map((config: StationSupplyRequirement) => config.station_name)).size,
    [stationConfigurations],
  );

  const stationOptions = useMemo(
    () =>
      Array.from(
        new Set<string>(
          stationConfigurations.map((config: StationSupplyRequirement) => config.station_name),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [stationConfigurations],
  );

  const replenishmentRecommendations = useMemo(
    () => replenishmentDashboard?.recommendations || [],
    [replenishmentDashboard],
  );

  const recentMovements = useMemo(
    () => replenishmentDashboard?.recent_movements || inventoryMovements,
    [replenishmentDashboard, inventoryMovements],
  );

  const alertHistory = useMemo(
    () => replenishmentDashboard?.alert_history || [],
    [replenishmentDashboard],
  );

  const openCreateMaterial = () => {
    setEditingMaterial(null);
    setMaterialForm(emptyMaterialForm);
    setIsMaterialDialogOpen(true);
  };

  const openEditMaterial = (item: InventoryItem) => {
    setEditingMaterial(item);
    setMaterialForm({
      name: item.name,
      sku: item.sku,
      unit_of_measure: item.unit_of_measure,
      current_stock: item.current_stock.toString(),
      minimum_stock: item.minimum_stock.toString(),
      purchase_price: item.purchase_price.toString(),
      reference_price: item.reference_price.toString(),
      description: item.description || "",
      es_material_cliente: item.es_material_cliente,
    });
    setIsMaterialDialogOpen(true);
  };

  const openCreateStationConfig = () => {
    setEditingStationConfig(null);
    setStationForm(emptyStationForm);
    setIsStationDialogOpen(true);
  };

  const openRestockDialog = (item?: InventoryItem) => {
    setRestockForm({
      inventory_id: item?.id || "",
      movement_type: "restock",
      quantity: "1",
      unit_cost: item ? item.purchase_price.toString() : "",
      notes: "",
    });
    setIsRestockDialogOpen(true);
  };

  const openConsumptionDialog = (stationName?: string) => {
    setConsumptionForm({
      station_name: stationName || stationOptions[0] || "",
      produced_units: "1",
      order_reference: "",
      notes: "",
    });
    setConsumptionStep("info");
    setIsConsumptionDialogOpen(true);
  };

  const openEditStationConfig = (config: StationSupplyRequirement) => {
    setEditingStationConfig(config);
    setStationForm({
      station_name: config.station_name,
      inventory_id: config.inventory_id,
      estimated_quantity: config.estimated_quantity.toString(),
      notes: config.notes || "",
    });
    setIsStationDialogOpen(true);
  };

  const handleSaveMaterial = async () => {
    if (!slug || !companyId || !materialForm.name || !materialForm.sku) return;

    const payload = {
      slug,
      company_id: companyId,
      name: materialForm.name.trim(),
      sku: materialForm.sku.trim(),
      unit_of_measure: materialForm.unit_of_measure,
      current_stock: Number(materialForm.current_stock || 0),
      minimum_stock: Number(materialForm.minimum_stock || 0),
      purchase_price: Number(materialForm.purchase_price || 0),
      reference_price: Number(materialForm.reference_price || 0),
      description: materialForm.description.trim(),
      es_material_cliente: materialForm.es_material_cliente,
    };

    if (editingMaterial) {
      await updateInventoryItem.mutateAsync({
        id: editingMaterial.id,
        data: payload,
      });
    } else {
      await createInventoryItem.mutateAsync(payload);
    }

    setIsMaterialDialogOpen(false);
  };

  const handleSaveStationConfig = async () => {
    if (!slug || !companyId || !stationForm.station_name || !stationForm.inventory_id) return;

    const payload = {
      slug,
      company_id: companyId,
      station_name: stationForm.station_name.trim(),
      inventory_id: stationForm.inventory_id,
      estimated_quantity: Number(stationForm.estimated_quantity || 0),
      notes: stationForm.notes.trim(),
    };

    if (editingStationConfig) {
      await updateStationConfiguration.mutateAsync({
        id: editingStationConfig.id,
        data: payload,
      });
    } else {
      await createStationConfiguration.mutateAsync(payload);
    }

    setIsStationDialogOpen(false);
  };

  const handleDeleteMaterial = async (item: InventoryItem) => {
    if (!companyId || !window.confirm(`¿Desactivar ${item.name}?`)) return;
    await deleteInventoryItem.mutateAsync({ id: item.id, company_id: companyId });
  };

  const handleDeleteStationConfig = async (config: StationSupplyRequirement) => {
    if (!companyId || !window.confirm(`¿Desactivar la configuración de ${config.station_name}?`)) return;
    await deleteStationConfiguration.mutateAsync({
      id: config.id,
      company_id: companyId,
    });
  };

  const handleSaveRestock = async () => {
    if (!slug || !companyId || !restockForm.inventory_id) return;

    await createInventoryMovement.mutateAsync({
      slug,
      company_id: companyId,
      inventory_id: restockForm.inventory_id,
      movement_type: restockForm.movement_type,
      quantity: Number(restockForm.quantity || 0),
      unit_cost: restockForm.unit_cost ? Number(restockForm.unit_cost) : undefined,
      notes: restockForm.notes.trim() || undefined,
    });

    setIsRestockDialogOpen(false);
  };

  const handlePrepareConsumptionReview = () => {
    const station = consumptionForm.station_name;
    const units = Number(consumptionForm.produced_units || 0);
    
    if (!station || units <= 0) return;

    const requirements = stationConfigurations.filter(
      (config: StationSupplyRequirement) => config.station_name === station
    );

    const items = requirements.map((req: StationSupplyRequirement) => ({
      inventory_id: req.inventory_id,
      material_name: req.inventoryItem?.name || "Desconocido",
      unit_of_measure: req.inventoryItem?.unit_of_measure || "u",
      estimated: req.estimated_quantity * units,
      real: req.estimated_quantity * units,
      waste: 0,
    }));

    setConsumedItems(items);
    setConsumptionStep("review");
  };

  const handleApplyConsumption = async () => {
    if (!slug || !companyId || consumedItems.length === 0) return;

    await consumeStationSupplies.mutateAsync({
      slug,
      company_id: companyId,
      station_name: consumptionForm.station_name,
      produced_units: Number(consumptionForm.produced_units || 0),
      order_reference: consumptionForm.order_reference.trim() || undefined,
      notes: consumptionForm.notes.trim() || undefined,
      consumed_items: consumedItems.map(item => ({
        inventory_id: item.inventory_id,
        quantity: item.real,
        waste: item.waste,
      })),
    });

    setIsConsumptionDialogOpen(false);
  };

  if (!canReadInventory) {
    return (
      <Card className="border-slate-200/70 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-900">Acceso restringido</CardTitle>
          <CardDescription>
            Este módulo de materia prima solo está disponible para roles con permiso de inventario.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Materia Prima e Insumos</h1>
          <p className="text-slate-600">
            Administra tu catálogo de materiales y la configuración de consumo por estación.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200/70 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Materiales activos</p>
                <p className="text-2xl font-bold text-slate-900">{inventoryItems.length}</p>
              </div>
              <PackageCheck className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Stock bajo</p>
                <p className="text-2xl font-bold text-rose-600">
                  {replenishmentDashboard?.summary.low_stock_items ?? lowStockItems.length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-rose-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Estaciones configuradas</p>
                <p className="text-2xl font-bold text-violet-600">{uniqueStationsCount}</p>
              </div>
              <Factory className="h-8 w-8 text-violet-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Costo sugerido de compra</p>
                <p className="text-2xl font-bold text-blue-600">
                  ${(replenishmentDashboard?.summary.estimated_reorder_cost ?? 0).toFixed(0)}
                </p>
              </div>
              <Truck className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1 border border-slate-200/60 w-fit rounded-xl gap-1">
          <TabsTrigger value="materials" className="rounded-lg">
            Catálogo
          </TabsTrigger>
          <TabsTrigger value="stations" className="rounded-lg">
            Configuración por estación
          </TabsTrigger>
          <TabsTrigger value="replenishment" className="rounded-lg">
            Reabastecimiento
          </TabsTrigger>
        </TabsList>

        <TabsContent value="materials" className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, SKU o unidad..."
                value={materialSearch}
                onChange={(event) => setMaterialSearch(event.target.value)}
                className="pl-9"
              />
            </div>

            {canManageInventory && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => openRestockDialog()}
                  className="border-slate-200"
                >
                  <ArrowDownToLine className="h-4 w-4 mr-2" />
                  Registrar reposición
                </Button>
                <Button onClick={openCreateMaterial} className="bg-black hover:bg-gray-800 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo material
                </Button>
              </div>
            )}
          </div>

          <Card className="border-slate-200/70 shadow-sm overflow-hidden">
            <CardHeader className="border-b border-slate-100 bg-white">
              <CardTitle className="text-slate-900">Catálogo de materia prima</CardTitle>
              <CardDescription>
                Consulta stock actual, mínimos de alerta y precios de referencia.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingInventoryItems ? (
                <TablePageSkeleton hasHeader={false} hasMetrics={false} columnCount={8} rowCount={10} />
              ) : filteredMaterials.length === 0 ? (
                <div className="p-8 text-center text-slate-500">No hay materiales registrados.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead>Material</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Unidad</TableHead>
                      <TableHead>Stock</TableHead>
                      <TableHead>Mínimo</TableHead>
                      <TableHead>Precios</TableHead>
                      <TableHead>Uso en estaciones</TableHead>
                      {canManageInventory && <TableHead className="text-right">Acciones</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMaterials.map((item: InventoryItem) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{item.name}</p>
                            <p className="text-xs text-slate-500">{item.description || "Sin descripción"}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-slate-600">{item.sku}</TableCell>
                        <TableCell>{item.unit_of_measure}</TableCell>
                        <TableCell>
                          <Badge className={getStockBadgeClass(item.is_low_stock)}>
                            {item.current_stock} {item.unit_of_measure}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.minimum_stock}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>Compra: ${item.purchase_price.toFixed(2)}</p>
                            <p className="text-slate-500">Referencia: ${item.reference_price.toFixed(2)}</p>
                          </div>
                        </TableCell>
                        <TableCell>{item.station_requirements_count}</TableCell>
                        {canManageInventory && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditMaterial(item)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openRestockDialog(item)}>
                                <ArrowDownToLine className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-rose-600 hover:text-rose-700"
                                onClick={() => handleDeleteMaterial(item)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stations" className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por estación o material..."
                value={stationSearch}
                onChange={(event) => setStationSearch(event.target.value)}
                className="pl-9"
              />
            </div>

            {canConfigureInventory && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => openConsumptionDialog()}
                  className="border-slate-200"
                >
                  <ArrowDownCircle className="h-4 w-4 mr-2" />
                  Registrar consumo
                </Button>
                <Button onClick={openCreateStationConfig} className="bg-black hover:bg-gray-800 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva configuración
                </Button>
              </div>
            )}
          </div>

          <Card className="border-slate-200/70 shadow-sm overflow-hidden">
            <CardHeader className="border-b border-slate-100 bg-white">
              <CardTitle className="text-slate-900">Consumo estimado por estación</CardTitle>
              <CardDescription>
                Define qué insumos necesita cada estación y cuánto consume por unidad producida.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingStationConfigurations ? (
                <TablePageSkeleton hasHeader={false} hasMetrics={false} columnCount={7} rowCount={8} />
              ) : filteredStationConfigurations.length === 0 ? (
                <div className="p-8 text-center text-slate-500">No hay configuraciones registradas.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead>Estación</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Consumo estimado</TableHead>
                      <TableHead>Stock actual</TableHead>
                      <TableHead>Notas</TableHead>
                      {canConfigureInventory && <TableHead className="text-right">Acciones</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStationConfigurations.map((config: StationSupplyRequirement) => (
                      <TableRow key={config.id}>
                        <TableCell className="font-medium text-slate-900">
                          {config.station_name}
                        </TableCell>
                        <TableCell>{config.inventoryItem?.name || "Material no disponible"}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-600">
                          {config.inventoryItem?.sku || "N/D"}
                        </TableCell>
                        <TableCell>
                          {config.estimated_quantity} {config.inventoryItem?.unit_of_measure || ""}
                        </TableCell>
                        <TableCell>
                          {config.inventoryItem ? (
                            <Badge className={getStockBadgeClass(config.inventoryItem.is_low_stock)}>
                              {config.inventoryItem.current_stock} {config.inventoryItem.unit_of_measure}
                            </Badge>
                          ) : (
                            "N/D"
                          )}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {config.notes || "Sin notas"}
                        </TableCell>
                        {canConfigureInventory && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditStationConfig(config)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openConsumptionDialog(config.station_name)}
                              >
                                <ArrowDownCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-rose-600 hover:text-rose-700"
                                onClick={() => handleDeleteStationConfig(config)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="replenishment" className="space-y-4">
          <InventoryAlertSettingsCard />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-slate-200/70 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Urgentes</p>
                    <p className="text-2xl font-bold text-rose-600">
                      {replenishmentDashboard?.summary.urgent_items ?? 0}
                    </p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-rose-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200/70 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Agotados</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {replenishmentDashboard?.summary.out_of_stock_items ?? 0}
                    </p>
                  </div>
                  <ClipboardList className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200/70 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Movimientos recientes</p>
                    <p className="text-2xl font-bold text-slate-900">{recentMovements.length}</p>
                  </div>
                  <Truck className="h-8 w-8 text-slate-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200/70 shadow-sm overflow-hidden">
            <CardHeader className="border-b border-slate-100 bg-white flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-slate-900">Tablero de reabastecimiento</CardTitle>
                <CardDescription>
                  Prioriza compras según stock mínimo, urgencia y costo estimado.
                </CardDescription>
              </div>
              {canManageInventory && (
                <Button onClick={() => openRestockDialog()} className="bg-emerald-600 hover:bg-emerald-700">
                  <ArrowDownToLine className="h-4 w-4 mr-2" />
                  Registrar reposición
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingReplenishmentDashboard ? (
                <TablePageSkeleton hasHeader={false} hasMetrics={false} columnCount={6} rowCount={8} />
              ) : replenishmentRecommendations.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No hay materiales por debajo del punto mínimo.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead>Material</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Stock / mínimo</TableHead>
                      <TableHead>Sugerencia</TableHead>
                      <TableHead>Costo estimado</TableHead>
                      {canManageInventory && <TableHead className="text-right">Acción</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {replenishmentRecommendations.map((item: ReplenishmentRecommendation) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{item.name}</p>
                            <p className="text-xs text-slate-500">
                              {item.sku} · {item.unit_of_measure}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getAlertLevelBadgeClass(item.alert_level)}>
                            {item.alert_level}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.current_stock} / {item.minimum_stock}
                        </TableCell>
                        <TableCell>
                          {item.suggested_restock_quantity} {item.unit_of_measure}
                        </TableCell>
                        <TableCell>${item.estimated_restock_cost.toFixed(2)}</TableCell>
                        {canManageInventory && (
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm" onClick={() => openRestockDialog(item)}>
                              Reponer
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200/70 shadow-sm overflow-hidden">
            <CardHeader className="border-b border-slate-100 bg-white">
              <CardTitle className="text-slate-900">Movimientos recientes</CardTitle>
              <CardDescription>
                Historial reciente de consumos, reposiciones y ajustes del inventario.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingInventoryMovements ? (
                <TablePageSkeleton hasHeader={false} hasMetrics={false} columnCount={6} rowCount={8} />
              ) : recentMovements.length === 0 ? (
                <div className="p-8 text-center text-slate-500">Todavía no hay movimientos registrados.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead>Fecha</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead>Cantidad</TableHead>
                      <TableHead>Stock antes / después</TableHead>
                      <TableHead>Referencia</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentMovements.map((movement: InventoryMovement) => (
                      <TableRow key={movement.id}>
                        <TableCell className="text-sm text-slate-600">
                          {formatDateTime(movement.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{formatMovementType(movement.movement_type)}</Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">
                              {movement.inventoryItem?.name || "Material no disponible"}
                            </p>
                            <p className="text-xs text-slate-500">
                              {movement.station_name || movement.order_reference || "Manual"}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {movement.quantity} {movement.inventoryItem?.unit_of_measure || ""}
                        </TableCell>
                        <TableCell>
                          {movement.previous_stock} → {movement.new_stock}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {movement.order_reference || movement.notes || "Sin referencia"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200/70 shadow-sm overflow-hidden">
            <CardHeader className="border-b border-slate-100 bg-white">
              <CardTitle className="text-slate-900">Historial de alertas enviadas</CardTitle>
              <CardDescription>
                Registro reciente de alertas automáticas de stock bajo procesadas por el sistema.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingReplenishmentDashboard ? (
                <TablePageSkeleton hasHeader={false} hasMetrics={false} columnCount={6} rowCount={8} />
              ) : alertHistory.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  Todavia no hay alertas automáticas registradas.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead>Fecha</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Destinatarios</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Criticos</TableHead>
                      <TableHead>Costo estimado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alertHistory.map((alert: InventoryAlertHistory) => (
                      <TableRow key={alert.id}>
                        <TableCell className="text-sm text-slate-600">
                          {formatDateTime(alert.triggered_at)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              alert.alert_status === "sent"
                                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                : "bg-rose-100 text-rose-700 border-rose-200"
                            }
                          >
                            {alert.alert_status === "sent" ? "Enviada" : "Fallida"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[320px] text-sm text-slate-600">
                            {alert.recipients.length > 0
                              ? alert.recipients.join(", ")
                              : "Sin destinatarios"}
                          </div>
                          {alert.error_message && (
                            <p className="text-xs text-rose-600 mt-1">{alert.error_message}</p>
                          )}
                        </TableCell>
                        <TableCell>{alert.low_stock_items_count}</TableCell>
                        <TableCell>{alert.critical_items_count}</TableCell>
                        <TableCell>${alert.estimated_reorder_cost.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isMaterialDialogOpen} onOpenChange={setIsMaterialDialogOpen}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>{editingMaterial ? "Editar material" : "Nuevo material"}</DialogTitle>
            <DialogDescription>
              Registra el insumo con su SKU, stock disponible, mínimos y precios base.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="inventory-name">Nombre</Label>
              <Input
                id="inventory-name"
                value={materialForm.name}
                onChange={(event) =>
                  setMaterialForm((current) => ({ ...current, name: event.target.value }))
                }
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="inventory-sku">SKU</Label>
                <Input
                  id="inventory-sku"
                  value={materialForm.sku}
                  onChange={(event) =>
                    setMaterialForm((current) => ({ ...current, sku: event.target.value }))
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label>Unidad de medida</Label>
                <Select
                  value={materialForm.unit_of_measure}
                  onValueChange={(value) =>
                    setMaterialForm((current) => ({ ...current, unit_of_measure: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una unidad" />
                  </SelectTrigger>
                  <SelectContent>
                    {INVENTORY_UNITS.map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="inventory-current-stock">Stock actual</Label>
                <Input
                  id="inventory-current-stock"
                  type="number"
                  min="0"
                  step="0.001"
                  value={materialForm.current_stock}
                  onChange={(event) =>
                    setMaterialForm((current) => ({
                      ...current,
                      current_stock: event.target.value,
                    }))
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="inventory-minimum-stock">Stock mínimo</Label>
                <Input
                  id="inventory-minimum-stock"
                  type="number"
                  min="0"
                  step="0.001"
                  value={materialForm.minimum_stock}
                  onChange={(event) =>
                    setMaterialForm((current) => ({
                      ...current,
                      minimum_stock: event.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="inventory-purchase-price">Precio de compra</Label>
                <Input
                  id="inventory-purchase-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={materialForm.purchase_price}
                  onChange={(event) =>
                    setMaterialForm((current) => ({
                      ...current,
                      purchase_price: event.target.value,
                    }))
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="inventory-reference-price">Precio referencia</Label>
                <Input
                  id="inventory-reference-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={materialForm.reference_price}
                  onChange={(event) =>
                    setMaterialForm((current) => ({
                      ...current,
                      reference_price: event.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="inventory-description">Descripción</Label>
              <Textarea
                id="inventory-description"
                value={materialForm.description}
                onChange={(event) =>
                  setMaterialForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </div>

            <div className="flex items-center justify-between space-x-2 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Material proporcionado por el cliente</Label>
                <p className="text-xs text-slate-500">
                  Si se activa, el consumo de este material no descontará stock ni afectará el costo de producción.
                </p>
              </div>
              <Switch
                checked={materialForm.es_material_cliente}
                onCheckedChange={(checked) =>
                  setMaterialForm((current) => ({ ...current, es_material_cliente: checked }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMaterialDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveMaterial}
              disabled={
                !materialForm.name ||
                !materialForm.sku ||
                createInventoryItem.isPending ||
                updateInventoryItem.isPending
              }
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <DollarSign className="h-4 w-4 mr-2" />
              {editingMaterial ? "Guardar cambios" : "Crear material"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isStationDialogOpen} onOpenChange={setIsStationDialogOpen}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>
              {editingStationConfig ? "Editar configuración" : "Nueva configuración por estación"}
            </DialogTitle>
            <DialogDescription>
              Define cuánto consume una estación de un material específico por unidad producida.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="station-name">Estación</Label>
              <Input
                id="station-name"
                placeholder="Ej: Impresión, Corte, Laminado"
                value={stationForm.station_name}
                onChange={(event) =>
                  setStationForm((current) => ({ ...current, station_name: event.target.value }))
                }
              />
            </div>

            <div className="grid gap-2">
              <Label>Material</Label>
              <Select
                value={stationForm.inventory_id}
                onValueChange={(value) =>
                  setStationForm((current) => ({ ...current, inventory_id: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un material" />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map((item: InventoryItem) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.sku}) - {item.current_stock} {item.unit_of_measure}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="estimated-quantity">Consumo estimado por unidad</Label>
              <Input
                id="estimated-quantity"
                type="number"
                min="0"
                step="0.001"
                value={stationForm.estimated_quantity}
                onChange={(event) =>
                  setStationForm((current) => ({
                    ...current,
                    estimated_quantity: event.target.value,
                  }))
                }
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="station-notes">Notas</Label>
              <Textarea
                id="station-notes"
                placeholder="Ej: consumo base por m2 o por pieza"
                value={stationForm.notes}
                onChange={(event) =>
                  setStationForm((current) => ({ ...current, notes: event.target.value }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStationDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveStationConfig}
              disabled={
                !stationForm.station_name ||
                !stationForm.inventory_id ||
                createStationConfiguration.isPending ||
                updateStationConfiguration.isPending
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Scale className="h-4 w-4 mr-2" />
              {editingStationConfig ? "Guardar cambios" : "Crear configuración"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isRestockDialogOpen} onOpenChange={setIsRestockDialogOpen}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Registrar movimiento de inventario</DialogTitle>
            <DialogDescription>
              Registra una reposición manual o un ajuste de stock con trazabilidad.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Material</Label>
              <Select
                value={restockForm.inventory_id}
                onValueChange={(value) =>
                  setRestockForm((current) => ({ ...current, inventory_id: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un material" />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map((item: InventoryItem) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.sku}) - {item.current_stock} {item.unit_of_measure}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Tipo de movimiento</Label>
                <Select
                  value={restockForm.movement_type}
                  onValueChange={(value: "restock" | "adjustment") =>
                    setRestockForm((current) => ({ ...current, movement_type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restock">Reposición</SelectItem>
                    <SelectItem value="adjustment">Ajuste</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="restock-quantity">Cantidad</Label>
                <Input
                  id="restock-quantity"
                  type="number"
                  min="0"
                  step="0.001"
                  value={restockForm.quantity}
                  onChange={(event) =>
                    setRestockForm((current) => ({ ...current, quantity: event.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="restock-unit-cost">Costo unitario</Label>
              <Input
                id="restock-unit-cost"
                type="number"
                min="0"
                step="0.01"
                value={restockForm.unit_cost}
                onChange={(event) =>
                  setRestockForm((current) => ({ ...current, unit_cost: event.target.value }))
                }
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="restock-notes">Notas</Label>
              <Textarea
                id="restock-notes"
                value={restockForm.notes}
                onChange={(event) =>
                  setRestockForm((current) => ({ ...current, notes: event.target.value }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRestockDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveRestock}
              disabled={
                !restockForm.inventory_id ||
                !restockForm.quantity ||
                createInventoryMovement.isPending
              }
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Truck className="h-4 w-4 mr-2" />
              Registrar movimiento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isConsumptionDialogOpen} onOpenChange={setIsConsumptionDialogOpen}>
        <DialogContent className={consumptionStep === "review" ? "sm:max-w-[800px]" : "sm:max-w-[600px]"}>
          <DialogHeader>
            <DialogTitle>Registrar consumo por estación</DialogTitle>
            <DialogDescription>
              {consumptionStep === "info" 
                ? "Paso 1: Define la estación y las unidades producidas para calcular el consumo estimado."
                : "Paso 2: Revisa y ajusta el consumo real y el desperdicio (merma) por cada material."}
            </DialogDescription>
          </DialogHeader>

          {consumptionStep === "info" ? (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Estación</Label>
                <Select
                  value={consumptionForm.station_name}
                  onValueChange={(value) =>
                    setConsumptionForm((current) => ({ ...current, station_name: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una estación" />
                  </SelectTrigger>
                  <SelectContent>
                    {stationOptions.map((stationName) => (
                      <SelectItem key={stationName} value={stationName}>
                        {stationName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="produced-units">Unidades producidas</Label>
                  <Input
                    id="produced-units"
                    type="number"
                    min="0"
                    step="0.001"
                    value={consumptionForm.produced_units}
                    onChange={(event) =>
                      setConsumptionForm((current) => ({
                        ...current,
                        produced_units: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="order-reference">Referencia de orden</Label>
                  <Input
                    id="order-reference"
                    placeholder="Ej: PLR-00124"
                    value={consumptionForm.order_reference}
                    onChange={(event) =>
                      setConsumptionForm((current) => ({
                        ...current,
                        order_reference: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="consumption-notes">Notas adicionales</Label>
                <Textarea
                  id="consumption-notes"
                  placeholder="Opcional: detalle del lote, observaciones, etc."
                  value={consumptionForm.notes}
                  onChange={(event) =>
                    setConsumptionForm((current) => ({ ...current, notes: event.target.value }))
                  }
                />
              </div>
            </div>
          ) : (
            <div className="py-4">
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-[200px]">Material</TableHead>
                      <TableHead>Estimado</TableHead>
                      <TableHead>Consumo Real</TableHead>
                      <TableHead>Desperdicio</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {consumedItems.map((item, index) => (
                      <TableRow key={item.inventory_id}>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium text-slate-900">{item.material_name}</p>
                            <p className="text-xs text-slate-500">{item.unit_of_measure}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {item.estimated.toFixed(3)}
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            step="0.001"
                            className="h-8 w-24"
                            value={item.real}
                            onChange={(e) => {
                              const newItems = [...consumedItems];
                              newItems[index].real = Number(e.target.value || 0);
                              setConsumedItems(newItems);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            step="0.001"
                            className="h-8 w-24 text-rose-600"
                            value={item.waste}
                            onChange={(e) => {
                              const newItems = [...consumedItems];
                              newItems[index].waste = Number(e.target.value || 0);
                              setConsumedItems(newItems);
                            }}
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium text-sm">
                          {(item.real + item.waste).toFixed(3)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  Confirma que las cantidades reales coinciden con el uso físico. El sistema descontará el (Consumo Real + Desperdicio) del inventario actual, a menos que el material sea proporcionado por el cliente.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between items-center sm:justify-between">
            {consumptionStep === "review" ? (
              <Button variant="ghost" onClick={() => setConsumptionStep("info")}>
                Atrás
              </Button>
            ) : (
              <div></div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsConsumptionDialogOpen(false)}>
                Cancelar
              </Button>
              {consumptionStep === "info" ? (
                <Button 
                  onClick={handlePrepareConsumptionReview}
                  disabled={!consumptionForm.station_name || Number(consumptionForm.produced_units || 0) <= 0}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                >
                  Continuar
                </Button>
              ) : (
                <Button
                  onClick={handleApplyConsumption}
                  disabled={consumeStationSupplies.isPending}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {consumeStationSupplies.isPending ? "Procesando..." : "Aplicar consumo"}
                  <CheckCircle2 className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

